import React, { useState } from 'react';
import { NavLink, Link, useLocation } from 'react-router-dom';
import { Search, User, ShoppingBag, Menu, X, ChevronDown } from 'lucide-react';

const NAV = [
  { to: '/', label: 'Home' },
  { to: '/shop', label: 'Shop', hasDropdown: true },
  { to: '/collections', label: 'Collections' },
  { to: '/our-story', label: 'Our Story' },
  { to: '/b2b', label: 'B2B' },
  { to: '/blog', label: 'Blog' },
  { to: '/contact', label: 'Contact' },
];

const SHOP_LINKS = [
  { to: '/shop?cat=shakes', label: 'Shakes' },
  { to: '/shop?cat=fruit-shakes', label: 'Fruit Shakes' },
  { to: '/shop?cat=cold-coffee', label: 'Cold Coffee' },
  { to: '/shop?cat=water-range', label: 'Water Range' },
  { to: '/shop?cat=chaas-range', label: 'Chaas Range' },
  { to: '/shop', label: 'View All' },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [shopOpen, setShopOpen] = useState(false);
  const location = useLocation();

  React.useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  React.useEffect(() => {
    setMobileOpen(false);
    setShopOpen(false);
  }, [location.pathname]);

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled ? 'backdrop-blur-2xl bg-ink-900/70 border-b border-bone-100/8' : 'bg-transparent'
      }`}
    >
      <div className="container-x flex items-center justify-between h-20 lg:h-24 gap-4">
        {/* Logo */}
        <Link to="/" className="group flex flex-col leading-none shrink-0" data-testid="logo-link">
          <span className="font-display italic text-2xl lg:text-3xl text-bone-50 group-hover:text-gold transition-colors">
            sip theory
          </span>
          <span className="text-[10px] tracking-[0.42em] text-bone-300 mt-0.5">BEVERAGES</span>
        </Link>

        {/* Desktop / laptop nav — visible from lg (1024px) up */}
        <nav className="hidden lg:flex items-center gap-5 lg:gap-7 xl:gap-9 flex-1 justify-center" data-testid="primary-nav">
          {NAV.map((item) => (
            <div
              key={item.to}
              className="relative"
              onMouseEnter={() => item.hasDropdown && setShopOpen(true)}
              onMouseLeave={() => item.hasDropdown && setShopOpen(false)}
            >
              <NavLink
                to={item.to}
                end={item.to === '/'}
                data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, '-')}`}
                className={({ isActive }) =>
                  `relative flex items-center gap-1 text-[11px] lg:text-xs tracking-[0.2em] lg:tracking-[0.22em] uppercase font-medium whitespace-nowrap transition-colors ${
                    isActive ? 'text-gold' : 'text-bone-100 hover:text-gold'
                  }`
                }
              >
                {({ isActive }) => (
                  <>
                    {item.label}
                    {item.hasDropdown && <ChevronDown className="w-3 h-3 opacity-70" />}
                    {isActive && (
                      <span className="absolute -bottom-2 left-1/2 -translate-x-1/2 w-6 h-px bg-gold" />
                    )}
                  </>
                )}
              </NavLink>

              {item.hasDropdown && shopOpen && (
                <div
                  data-testid="shop-dropdown"
                  className="absolute top-full left-1/2 -translate-x-1/2 pt-4 w-56"
                >
                  <div className="glass-card p-2 rounded-2xl">
                    {SHOP_LINKS.map((s) => (
                      <Link
                        key={s.to}
                        to={s.to}
                        className="block px-4 py-2.5 text-sm text-bone-200 hover:text-gold hover:bg-white/[0.04] rounded-xl transition-colors"
                      >
                        {s.label}
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}
        </nav>

        {/* Right cluster */}
        <div className="flex items-center gap-2 lg:gap-3 shrink-0">
          <button aria-label="Search" className="btn-ghost-icon hidden lg:inline-flex" data-testid="nav-search">
            <Search className="w-4 h-4" />
          </button>
          <Link to="/account" aria-label="Account" className="btn-ghost-icon hidden lg:inline-flex" data-testid="nav-account">
            <User className="w-4 h-4" />
          </Link>
          <Link to="/cart" aria-label="Cart" className="btn-ghost-icon relative" data-testid="nav-cart">
            <ShoppingBag className="w-4 h-4" />
            <span className="absolute -top-1 -right-1 w-4 h-4 text-[10px] rounded-full bg-gold text-ink-900 flex items-center justify-center font-semibold">2</span>
          </Link>
          <Link to="/shop" className="btn-outline !py-2.5 !px-5 hidden xl:inline-flex" data-testid="nav-order">
            Order Now
          </Link>
          <button
            aria-label="Menu"
            className="lg:hidden btn-ghost-icon"
            onClick={() => setMobileOpen((v) => !v)}
            data-testid="mobile-menu-toggle"
          >
            {mobileOpen ? <X className="w-4 h-4" /> : <Menu className="w-4 h-4" />}
          </button>
        </div>
      </div>

      {/* Mobile drawer */}
      {mobileOpen && (
        <div className="lg:hidden border-t border-bone-100/8 bg-ink-950/95 backdrop-blur-2xl" data-testid="mobile-menu">
          <div className="px-6 py-6 space-y-1">
            {NAV.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === '/'}
                className={({ isActive }) =>
                  `block py-3 text-sm tracking-[0.22em] uppercase border-b border-bone-100/5 ${
                    isActive ? 'text-gold' : 'text-bone-100'
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
            <div className="pt-4">
              <Link to="/shop" className="btn-primary w-full justify-center" data-testid="mobile-order">
                Order Now
              </Link>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
